import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Plus,
  Minus,
  HelpCircle,
  MessageSquare,
  Mail,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";
import faqImage from "../assets/images/faq.webp";
import Meta from "../components/Meta/Meta";

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "What is Maxipitch and how does it work?",
      answer:
        "Maxipitch is a powerful B2B lead generation and email marketing platform that uses AI to help you find and connect with potential customers. Simply input your target criteria, and our system will generate high-quality leads, create personalized email campaigns, and automate your outreach process.",
    },
    {
      question: "Do I need to sign up or add a card for a Free Search?",
      answer:
        "No. You can try a search without login or payment, and you do not need to add a card.",
    },
    {
      question: "What email id is used to send mails?",
      answer:
        "Emails are sent directly from your connected account (Gmail, Outlook, or custom domain). We integrate securely and follow the limits set by your email service provider.",
    },
    {
      question: "How secure is my data with Maxipitch?",
      answer:
        "Your data security is our top priority. We use industry-standard encryption for data in transit and at rest, along with TAC security measures. We do not share your data with any third parties under any circumstances.",
    },
    {
      question: "How does Maxipitch keep emails out of spam folders?",
      answer:
        "We follow best practices to maintain high deliverability. We limit sending to 50 emails per day, use AI-generated personalized templates, and we check spam scores before sending mails   to ensure emails do not end up in spam folders.",
    },
    {
      question: "Does Maxipitch offer a free trial?",
      answer:
        "Yes, Maxipitch offers a free plan that allows you to send up to 50 emails.",
    },
    {
      question: "Does Maxipitch provide bulk contact data in Excel format?",
      answer:
        "Yes, bulk contact data can be provided in Excel format. This feature is available under our Enterprise Plan. Please visit our pricing page or Contact Us page to learn more, or reach us directly at support@maxipitch.com.",
    },
    {
      question: "Does Maxipitch support global outreach?",
      answer:
        "Yes. Maxipitch is designed to support outreach globally, allowing you to target and connect with audiences across any region or market.",
    },
    {
      question: "How accurate are the leads generated by Maxipitch?",
      answer:
        "Our smart AI lead discovery system ensures highly reliable results with an accuracy rate exceeding 95%. We continuously update our database and algorithms to ensure you get the most relevant and up-to-date contact information for your target audience.",
    },
    {
      question: "What kind of support does Maxipitch offer?",
      answer:
        "Maxipitch offers comprehensive support including 24/7 email assistance and an extensive knowledge base. You can also reach out via our Contact Us page or email us at support@maxipitch.com.",
    },
    {
      question: "How does Maxipitch ensure email deliverability?",
      answer:
        "Maxipitch uses advanced email deliverability techniques including domain authentication, spam score checking.",
    },
    {
      question: "Can I customize the email templates?",
      answer:
        "Absolutely! Maxipitch offers customizable email templates. You can edit existing ones or create your own. If you update a template during an active campaign, all future emails will use the updated version, so we recommend finalizing templates before starting a campaign.",
    },
    {
      question: "Does Maxipitch improve the quality of your sales pipeline?",
      answer:
        "Yes, Maxipitch improves the quality of your sales pipeline by combining AI-powered personalization, verified contact data, and smart outreach controls. This helps you connect with the right people, increase response rates, and build a more qualified and consistent pipeline.",
    },
  ];

  // Floating animation variants
  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
    hover: {
      scale: 1.03,
      transition: { duration: 0.3 },
    },
  };

  const iconVariants = {
    animate: {
      rotate: [0, 10, 0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatDelay: 2,
      },
    },
  };

  // Animation variants for FAQ answer
  const answerVariants = {
    open: {
      opacity: 1,
      height: "auto",
      marginTop: "16px",
    },
    closed: {
      opacity: 0,
      height: 0,
      marginTop: "0px",
    },
  };

  // Simple navigation function instead of Link component
  const navigateTo = (path) => {
    window.location.href = path;
  };

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden">
      {/* Hero Section with Unique Design */}
      <Meta
        title="MaxiPitch FAQ | Sales Automation Platform Help"
        description="Find answers to common questions about MaxiPitch AI sales automation platform, including features, pricing, lead generation, and outbound marketing tools."
        url="https://maxipitch.com/faq"
      />
      <Header />
      <section className="relative py-14 max-md:py-8 px-4 overflow-hidden bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5">
        {/* Background Elements */}

        <div className="absolute inset-0 z-0">
          {/* Geometric Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgdmlld0JveD0iMCAwIDgwIDgwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNNDAgNDBjMC0xLjY1Njg1NCAxLjM0MzE0Ni0zIDMtM3MzIDEuMzQzMTQ2IDMgMy0xLjM0MzE0NiAzLTMgMy0zLTEuMzQzMTQ2LTMtM3ptMCAxNGMwLTEuNjU2ODU0IDEuMzQzMTQ2LTMgMy0zczMgMS4zNDMxNDYgMyAzLTEuMzQzMTQ2IDMtMyAzLTMtMS4zNDMxNDYtMy0zem0tMTQgMGMwLTEuNjU2ODU0IDEuMzQzMTQ2LTMgMy0zczMgMS4zNDMxNDYgMyAzLTEuMzQzMTQ2IDMtMyAzLTMtMS4zNDMxNDYtMy0zem0wLTE0YzAtMS42NTY4NTQgMS4zNDMxNDYtMyAzLTMgMyAxLjM0MzE0NiAzIDMgMy0xLjM0MzE0NiAzLTMgMy0zLTEuMzQzMTQ2LTMtM3oiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
          </div>

          {/* Floating Question Marks */}
          <motion.div
            className="absolute top-20 left-10 text-6xl text-gray-500 opacity-30"
            variants={floatingVariants}
            animate="animate"
          >
            ?
          </motion.div>
          <motion.div
            className="absolute top-40 right-20 text-8xl text-gray-500 opacity-20"
            variants={floatingVariants}
            animate="animate"
            style={{ transitionDelay: "1s" }}
          >
            ?
          </motion.div>
          <motion.div
            className="absolute bottom-20 left-1/4 text-5xl text-gray-200 opacity-25"
            variants={floatingVariants}
            animate="animate"
            style={{ transitionDelay: "2s" }}
          >
            ?
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 ">
          <div className="flex flex-col lg:flex-row items-center gap-16 ">
            {/* Left Column - Content */}
            <motion.div
              className="lg:w-1/2"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center rounded-full  px-4 py-2 text-sm max-md:text-xs font-medium text-black border border-indigo-500 mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Frequently Asked Questions
              </motion.div>

              <motion.h1
                className=""
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Questions?
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  We've Got Answers
                </span>
              </motion.h1>

              <motion.p
                className="text-xl max-md:text-base text-gray-600 mb-10 max-md:mb-4 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Everything you need to know about Maxipitch, all in one place.
              </motion.p>

              <motion.div
                className="flex flex-col gap-4 max-md:items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  variant="outline"
                  size="lg"
                  className=" text-white main-button transition-all duration-300 px-6 py-3  hover:text-white max-md:w-44 w-56 md:text-lg"
                  onClick={() => navigateTo("/contact-us")}
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Contact Support
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Column - Visual Element with Background Image */}
            <motion.div
              className="relative w-full lg:w-1/2 max-md:hidden"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {/* Background Image Container */}
              <div
                className="absolute inset-0 bg-cover bg-center rounded-2xl overflow-hidden  h-[250px] sm:h-[350px] lg:h-full"
                style={{
                  backgroundImage: `url(${faqImage})`,
                }}
              ></div>

              {/* Content Layer */}
              <div className="relative z-10 flex flex-col items-center justify-center h-[250px] sm:h-[350px] lg:min-h-[400px]">
                {/* Large Question Mark */}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-14 max-md:py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-md:mb-8"
          >
            <h2 className="">Common Questions</h2>
            <p className="text-xl max-md:text-base text-gray-600 max-w-2xl mx-auto">
              Find answers to the most frequently asked questions about our
              platform
            </p>
          </motion.div>

          {/* ✅ Add items-start so each card has independent height */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover="hover"
                className={`bg-white rounded-lg shadow-sm overflow-hidden border border-indigo-300 transition-all duration-300 ${
                  openIndex === index ? "ring-2 ring-black" : ""
                }`}
              >
                {/* Question row */}
                <button
                  className="flex justify-between items-center align-middle w-full p-3 max-md:p-3 text-left focus:outline-none"
                  onClick={() => toggleFAQ(index)}
                >
                  <h3 className="text-lg font-medium max-md:text-base">
                    {faq.question}
                  </h3>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0 w-8 h-8 main-button font-bold rounded-full flex items-center justify-center"
                  >
                    <Plus className="h-5 w-5 text-white font-extrabold" />
                  </motion.div>
                </button>

                {/* Answer section (only expands this card) */}
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="px-6 pb-4"
                    >
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FAQPage;
